import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--tg-theme-bg-color, #17212b)',
    color: 'var(--tg-theme-text-color, #ffffff)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", Roboto, "Helvetica Neue", sans-serif',
    padding: '20px 16px 48px',
    boxSizing: 'border-box',
  },
  header: {
    fontSize: 28,
    fontWeight: 700,
    margin: '8px 0 4px',
  },
  subheader: {
    fontSize: 15,
    color: 'var(--tg-theme-hint-color, #9aa6b2)',
    marginBottom: 24,
    lineHeight: 1.4,
  },
  fieldBlock: { marginBottom: 22 },
  label: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
    display: 'block',
  },
  required: { color: '#ff5252' },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    background: 'var(--tg-theme-secondary-bg-color, #232e3c)',
    border: '1px solid transparent',
    color: 'var(--tg-theme-text-color, #ffffff)',
    borderRadius: 12,
    padding: '14px 14px',
    fontSize: 16,
    outline: 'none',
  },
  textarea: {
    minHeight: 90,
    resize: 'vertical',
  },
  select: {
    appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 24 24\' fill=\'%239aa6b2\'><path d=\'M7 10l5 5 5-5z\'/></svg>")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    paddingRight: 36,
  },
  radioRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    cursor: 'pointer',
    fontSize: 16,
  },
  radioCircle: {
    width: 20, height: 20,
    borderRadius: '50%',
    border: '2px solid var(--tg-theme-hint-color, #5a6776)',
    marginRight: 14,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  radioCircleActive: {
    borderColor: 'var(--tg-theme-button-color, #2ea6ff)',
  },
  radioDot: {
    width: 10, height: 10,
    borderRadius: '50%',
    background: 'var(--tg-theme-button-color, #2ea6ff)',
  },
  hint: {
    color: 'var(--tg-theme-hint-color, #9aa6b2)',
    fontSize: 13,
    marginTop: 6,
  },
  errorMsg: {
    color: '#ff6b6b',
    fontSize: 13,
    marginTop: 6,
  },
  submitBtn: {
    width: '100%',
    background: 'var(--tg-theme-button-color, #2ea6ff)',
    color: 'var(--tg-theme-button-text-color, #ffffff)',
    border: 'none',
    padding: '16px',
    fontSize: 17,
    fontWeight: 600,
    borderRadius: 12,
    marginTop: 16,
    cursor: 'pointer',
  },
  submitBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  successCard: {
    background: 'var(--tg-theme-secondary-bg-color, #232e3c)',
    borderRadius: 16,
    padding: 32,
    textAlign: 'center',
    margin: '40px auto',
    maxWidth: 480,
  },
};

function getTg() {
  if (typeof window === 'undefined') return null;
  return window.Telegram && window.Telegram.WebApp ? window.Telegram.WebApp : null;
}

function FieldRenderer({ field, value, onChange }) {
  const id = `f_${field.id}`;
  const required = !!field.required;

  if (field.type === 'select') {
    return (
      <select
        id={id}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        style={{ ...styles.input, ...styles.select }}
      >
        <option value="" disabled>{field.placeholder || 'Выберите вариант'}</option>
        {(field.options || []).map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }

  if (field.type === 'radio') {
    return (
      <div>
        {(field.options || []).map(opt => {
          const active = value === opt;
          return (
            <div key={opt} style={styles.radioRow} onClick={() => onChange(opt)}>
              <div style={{ ...styles.radioCircle, ...(active ? styles.radioCircleActive : {}) }}>
                {active && <div style={styles.radioDot} />}
              </div>
              <div>{opt}</div>
            </div>
          );
        })}
      </div>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <div style={styles.radioRow} onClick={() => onChange(value === 'Да' ? 'Нет' : 'Да')}>
        <div style={{ ...styles.radioCircle, ...(value === 'Да' ? styles.radioCircleActive : {}) }}>
          {value === 'Да' && <div style={styles.radioDot} />}
        </div>
        <div>{field.label}</div>
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <textarea
        id={id}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={field.placeholder || ''}
        required={required}
        style={{ ...styles.input, ...styles.textarea }}
      />
    );
  }

  const inputType = field.type === 'email' ? 'email' : field.type === 'tel' ? 'tel' : 'text';
  return (
    <input
      id={id}
      type={inputType}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder || ''}
      required={required}
      style={styles.input}
    />
  );
}

export default function JoinForm() {
  const { communityId } = useParams();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const tg = getTg();

  useEffect(() => {
    if (tg) {
      try { tg.ready(); tg.expand(); } catch (_) {}
    }
  }, [tg]);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    fetch(`${API_BASE}/api/public/communities/${communityId}/join-schema`)
      .then(async r => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.detail || `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then(data => {
        if (aborted) return;
        setSchema(data);
        setLoading(false);
      })
      .catch(e => {
        if (aborted) return;
        setLoadError(e.message || 'Не удалось загрузить форму');
        setLoading(false);
      });
    return () => { aborted = true; };
  }, [communityId]);

  // Build the dynamic field list with follow-up fields injected when their trigger is selected.
  const visibleFields = useMemo(() => {
    if (!schema) return [];
    const out = [];
    for (const f of schema.fields || []) {
      out.push(f);
      const fu = f.follow_up_field;
      if (fu && values[f.label] === fu.trigger_value) {
        out.push({
          id: fu.id || `${f.id}_followup`,
          label: fu.label || `Уточните: ${f.label}`,
          type: fu.type || 'text',
          required: fu.required !== false,
          placeholder: fu.placeholder || '',
        });
      }
    }
    return out;
  }, [schema, values]);

  const setValue = (label, v) => {
    setValues(prev => ({ ...prev, [label]: v }));
    setErrors(prev => {
      if (!prev[label]) return prev;
      const next = { ...prev };
      delete next[label];
      return next;
    });
  };

  const validate = () => {
    const errs = {};
    for (const f of visibleFields) {
      if (!f.required) continue;
      const v = values[f.label];
      if (v === undefined || v === null || String(v).trim() === '') {
        errs[f.label] = 'Поле обязательное';
      } else if (f.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v))) {
        errs[f.label] = 'Некорректный email';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      try { tg?.HapticFeedback?.notificationOccurred?.('error'); } catch (_) {}
      return;
    }
    if (!tg || !tg.initData) {
      setLoadError('Эта форма открывается только из Telegram');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/applications/${communityId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          init_data: tg.initData,
          form_data: visibleFields.reduce((acc, f) => {
            const v = values[f.label];
            if (v !== undefined && v !== null && String(v) !== '') acc[f.label] = v;
            return acc;
          }, {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setSubmitted(data);
      try { tg?.HapticFeedback?.notificationOccurred?.('success'); } catch (_) {}
    } catch (e) {
      setLoadError(e.message || 'Не удалось отправить заявку');
      try { tg?.HapticFeedback?.notificationOccurred?.('error'); } catch (_) {}
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ marginTop: 80, textAlign: 'center' }}>Загрузка формы…</div>
      </div>
    );
  }

  if (loadError && !submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.successCard}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 17, marginBottom: 8 }}>{loadError}</div>
          {!tg && (
            <div style={styles.hint}>
              Откройте эту страницу из Telegram через бота
            </div>
          )}
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.successCard}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Заявка отправлена</div>
          <div style={styles.hint}>
            {submitted.require_approval
              ? 'Мы рассмотрим её и пришлём вам ссылку для вступления.'
              : 'Ссылка-приглашение будет отправлена вам в боте.'}
          </div>
          <button
            style={styles.submitBtn}
            onClick={() => { try { tg?.close(); } catch (_) {} }}
          >
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>{schema.community_name || 'Регистрация'}</div>
      <div style={styles.subheader}>{schema.welcome_text}</div>

      {visibleFields.map(f => (
        <div key={f.id} style={styles.fieldBlock}>
          {f.type !== 'checkbox' && (
            <label style={styles.label} htmlFor={`f_${f.id}`}>
              {f.label}{f.required && <span style={styles.required}> *</span>}
            </label>
          )}
          <FieldRenderer
            field={f}
            value={values[f.label] || ''}
            onChange={v => setValue(f.label, v)}
          />
          {f.description && <div style={styles.hint}>{f.description}</div>}
          {errors[f.label] && <div style={styles.errorMsg}>{errors[f.label]}</div>}
        </div>
      ))}

      <button
        onClick={handleSubmit}
        disabled={submitting}
        style={{ ...styles.submitBtn, ...(submitting ? styles.submitBtnDisabled : {}) }}
      >
        {submitting ? 'Отправка…' : 'Отправить заявку'}
      </button>
    </div>
  );
}
